import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Form, Input, Button, Card, Typography, Row, Col, Upload,
  message, Modal, Spin, Select, Alert, InputNumber, Rate, Slider, Space,
} from "antd";
import ImgCrop from "antd-img-crop";
import {
  InboxOutlined, DeleteOutlined, LeftOutlined, RightOutlined,
  FacebookOutlined, TwitterOutlined, LinkedinOutlined,
  InstagramOutlined, YoutubeOutlined, ArrowLeftOutlined, EditOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getSingleEmployerAccount, generateSignedUrl,
  createEmployerAccount, updateEmployerAccount,
} from "../../features/ptmAccounts/ptmAccountSlice";
import "../../assets/css/create-ptm-form.css";


const { Title, Text } = Typography;
const { Option } = Select;
const HEADING_MAX_LENGTH = 44;
const DESCRIPTION_MAX_LENGTH = 150;
const CULTURE_MAX_IMAGES = 3;
const WHATWEDO_MAX_IMAGES = 2;
const BASE_URL = process.env.REACT_APP_S3_URL || "https://skillgigs-v3-prod-documents-cl.s3.us-east-2.amazonaws.com";
const NO_IMAGE = `${BASE_URL}/content/img/no-pic.png`;

const SOCIAL_CONFIG = [
  { key: "facebook", Icon: FacebookOutlined, color: "#1877f2", label: "Facebook" },
  { key: "twitter", Icon: TwitterOutlined, color: "#1da1f2", label: "X (Twitter)" },
  { key: "linkedin", Icon: LinkedinOutlined, color: "#0a66c2", label: "LinkedIn" },
  { key: "instagram", Icon: InstagramOutlined, color: "#e4405f", label: "Instagram" },
  { key: "youtube", Icon: YoutubeOutlined, color: "#ff0000", label: "YouTube" },
];

const MAX_LOGO_WIDTH = 254;
const MAX_LOGO_HEIGHT = 254;
const WEB_BANNER_WIDTH = 1920;
const WEB_BANNER_HEIGHT = 780;
const MOBILE_BANNER_WIDTH = 390;
const MOBILE_BANNER_HEIGHT = 568;
const CULTURE_WIDTH = 490;
const CULTURE_HEIGHT = 230;
const WHATWEDO_WIDTH = 736;
const WHATWEDO_HEIGHT = 250;
const MOBILE_LOGO_WIDTH = 52;
const MOBILE_LOGO_HEIGHT = 52;

const CROP_CANVAS_W = 480;
const CROP_CANVAS_H = 340;

const buildImageUrl = (path) => {
  if (!path || path.startsWith("http")) return path || "";
  return `${BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

const validateSocialUrl = (key, label, host) => (_, value) => {
  if (!value || value.trim() === "") return Promise.resolve();
  if (!value.startsWith("https://"))
    return Promise.reject(new Error(`URL must start with https://...`));
  if (value.length <= 10) return Promise.resolve();
  try {
    const url = new URL(value);
    const domain = url.hostname.replace(/^www\./, "");
    if (key === "twitter") {
      return domain === "twitter.com" || domain === "x.com"
        ? Promise.resolve()
        : Promise.reject(new Error(`Please enter a valid ${label} URL (twitter.com or x.com)`));
    }
    if (!domain.includes(host))
      return Promise.reject(new Error(`Please enter a valid ${label} URL (https://www.${key}.com/...)`));
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error(`Please enter a valid ${label} URL`));
  }
};

const validateWebsiteUrl = (_, value) => {
  if (!value || value.trim() === "") return Promise.resolve();
  try {
    const url = new URL(value);
    if (url.protocol !== "https:")
      return Promise.reject(new Error("URL must start with https://"));
    if (!url.hostname.includes("."))
      return Promise.reject(new Error("Please enter a valid website URL"));
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error("Please enter a valid website URL"));
  }
};

const extractKeyFromUrl = (url) => {
  if (!url) return "";
  if (!url.startsWith("http")) return url;
  return url.replace(/^https?:\/\/[^\/]+\//, "");
};

const validateFileType = (file, allowedTypes, label = "File") => {
  const ext = file.name.split(".").pop().toLowerCase();
  const mimeMap = {
    jpeg: "image/jpeg", jpg: "image/jpeg", png: "image/png",
    webp: "image/webp", mp4: "video/mp4", webm: "video/webm",
  };
  const isValid = allowedTypes.some((type) => {
    const expectedMime = mimeMap[type] || type;
    return file.type === expectedMime || ext === type;
  });
  if (!isValid) {
    message.error(`${label} type not supported. Allowed types: ${allowedTypes.join(", ")}`);
    return false;
  }
  return true;
};

const truncateText = (text, max) => (text?.length > max ? `${text.slice(0, max)}...` : text || "");

const checkImageDimensions = (file, maxWidth, maxHeight) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      if (img.naturalWidth > maxWidth || img.naturalHeight > maxHeight) {
        message.error(`Image dimensions must be width ${maxWidth}px and height ${maxHeight}px or smaller.`);
        reject();
      } else {
        resolve(file);
      }
    };
    img.onerror = () => { message.error("Error loading image."); reject(); };
  });


const LogoCropModal = ({ open, file, onOk, onCancel, defaultCropW = 254, defaultCropH = 254 }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [cropW, setCropW] = useState(defaultCropW);
  const [cropH, setCropH] = useState(defaultCropH);
  const [lockAspect, setLockAspect] = useState(false);
  const [hasTransparency, setHasTransparency] = useState(false);
  const dragging = useRef(false);
  const dragStart = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!file || !open) return;
    const url = URL.createObjectURL(file);
    setImgSrc(url);
    setCropW(defaultCropW);
    setCropH(defaultCropH);

    const img = new Image();
    img.onload = () => {
      setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
      const s = Math.max(defaultCropW / img.naturalWidth, defaultCropH / img.naturalHeight);
      setScale(parseFloat(s.toFixed(3)));
      setPos({
        x: (CROP_CANVAS_W - img.naturalWidth * s) / 2,
        y: (CROP_CANVAS_H - img.naturalHeight * s) / 2,
      });
      const offscreen = document.createElement("canvas");
      offscreen.width = img.naturalWidth;
      offscreen.height = img.naturalHeight;
      const octx = offscreen.getContext("2d");
      octx.drawImage(img, 0, 0);
      try {
        const { data } = octx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
        let transparent = false;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) { transparent = true; break; }
        }
        setHasTransparency(transparent);
      } catch {
        setHasTransparency(false);
      }
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  const cropX = (CROP_CANVAS_W - Math.min(cropW, CROP_CANVAS_W)) / 2;
  const cropY = (CROP_CANVAS_H - Math.min(cropH, CROP_CANVAS_H)) / 2;
  const displayCropW = Math.min(cropW, CROP_CANVAS_W);
  const displayCropH = Math.min(cropH, CROP_CANVAS_H);

  const onMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  };
  const onMouseMove = (e) => {
    if (!dragging.current || !dragStart.current) return;
    setPos({
      x: dragStart.current.px + e.clientX - dragStart.current.mx,
      y: dragStart.current.py + e.clientY - dragStart.current.my,
    });
  };
  const onMouseUp = () => { dragging.current = false; };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    dragging.current = true;
    dragStart.current = { mx: t.clientX, my: t.clientY, px: pos.x, py: pos.y };
  };
  const onTouchMove = (e) => {
    if (!dragging.current || !dragStart.current) return;
    const t = e.touches[0];
    setPos({
      x: dragStart.current.px + t.clientX - dragStart.current.mx,
      y: dragStart.current.py + t.clientY - dragStart.current.my,
    });
  };

  const handleZoomChange = (newScale) => {
    const cx = CROP_CANVAS_W / 2, cy = CROP_CANVAS_H / 2;
    setPos(p => ({
      x: cx - (cx - p.x) * (newScale / scale),
      y: cy - (cy - p.y) * (newScale / scale),
    }));
    setScale(newScale);
  };

  const handleCropWChange = (val) => {
    const v = val || 1;
    if (lockAspect && cropW > 0) setCropH(Math.round(v * (cropH / cropW)));
    setCropW(v);
  };
  const handleCropHChange = (val) => {
    const v = val || 1;
    if (lockAspect && cropH > 0) setCropW(Math.round(v * (cropW / cropH)));
    setCropH(v);
  };

  const fitImage = () => {
    if (!imgNatural.w) return;
    const s = Math.max(cropW / imgNatural.w, cropH / imgNatural.h);
    setScale(parseFloat(s.toFixed(3)));
    setPos({
      x: (CROP_CANVAS_W - imgNatural.w * s) / 2,
      y: (CROP_CANVAS_H - imgNatural.h * s) / 2,
    });
  };


  const handleExport = () => {
    if (!imgSrc) return;
    const srcX = (cropX - pos.x) / scale;
    const srcY = (cropY - pos.y) / scale;
    const srcW = cropW / scale;
    const srcH = cropH / scale;

    const exportImg = new Image();
    exportImg.crossOrigin = "anonymous";
    exportImg.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = defaultCropW;
      canvas.height = defaultCropH;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = hasTransparency ? "#0f1e2b" : "#ffffff";
      ctx.fillRect(0, 0, defaultCropW, defaultCropH);
      ctx.drawImage(exportImg, srcX, srcY, srcW, srcH, 0, 0, defaultCropW, defaultCropH);
      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], "logo.png", { type: "image/png" });
        onOk(croppedFile);
      }, "image/png");
    };
    exportImg.src = imgSrc;
  };

  const minScale = imgNatural.w
    ? parseFloat((0.1).toFixed(3))
    : 0.1;
  const maxScale = imgNatural.w
    ? parseFloat((Math.max(cropW / imgNatural.w, cropH / imgNatural.h) * 8).toFixed(3))
    : 10;

  return (
    <Modal
      title="Logo Crop & Resize"
      open={open}
      onCancel={onCancel}
      width={560}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="ok" type="primary" icon={<EditOutlined />} onClick={handleExport}>
          Crop & Upload
        </Button>,
      ]}
    >
      {/* <Space wrap style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 13 }}>Width (px):</span>
        <InputNumber min={10} max={4000} value={cropW} onChange={handleCropWChange} style={{ width: 80 }} />
        <span style={{ fontSize: 13 }}>Height (px):</span>
        <InputNumber min={10} max={4000} value={cropH} onChange={handleCropHChange} style={{ width: 80 }} />
        <Button size="small" onClick={() => setLockAspect(l => !l)}
          type={lockAspect ? "primary" : "default"}>
          {lockAspect ? "🔒 Locked" : "🔓 Free"}
        </Button>
        <Button size="small" onClick={fitImage}>Fit Image</Button>
      </Space> */}

      {/* Canvas */}
      <div
        style={{
          position: "relative",
          width: CROP_CANVAS_W,
          height: CROP_CANVAS_H,
          background: "repeating-conic-gradient(#808080 0% 25%, #606060 0% 50%) 0 0 / 16px 16px",
          borderRadius: 8,
          overflow: "hidden",
          cursor: dragging.current ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          border: "1px solid #d9d9d9",
          margin: "0 auto",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={() => { dragging.current = false; }}
      >
        {/* The actual image — positioned via pos state */}
        {imgSrc && (
          <img
            ref={imgRef}
            src={imgSrc}
            alt="crop-preview"
            draggable={false}
            style={{
              position: "absolute",
              left: pos.x,
              top: pos.y,
              width: imgNatural.w * scale,
              height: imgNatural.h * scale,
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        )}

        {/* Overlay: dark mask with transparent crop window + dashed border */}
        <svg
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          width={CROP_CANVAS_W}
          height={CROP_CANVAS_H}
        >
          <defs>
            <mask id="crop-mask">
              <rect width={CROP_CANVAS_W} height={CROP_CANVAS_H} fill="white" />
              <rect x={cropX} y={cropY} width={displayCropW} height={displayCropH} fill="black" />
            </mask>
          </defs>
          <rect
            width={CROP_CANVAS_W}
            height={CROP_CANVAS_H}
            fill="rgba(0,0,0,0.52)"
            mask="url(#crop-mask)"
          />
          <rect
            x={cropX} y={cropY}
            width={displayCropW} height={displayCropH}
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeDasharray="6 3"
          />
          {[
            [cropX, cropY],
            [cropX + displayCropW, cropY],
            [cropX, cropY + displayCropH],
            [cropX + displayCropW, cropY + displayCropH],
          ].map(([cx, cy], i) => (
            <rect key={i} x={cx - 4} y={cy - 4} width={8} height={8} fill="white" rx={2} />
          ))}
          {/* Rule of thirds grid lines */}
          {[1, 2].map(n => (
            <React.Fragment key={n}>
              <line
                x1={cropX + (displayCropW / 3) * n} y1={cropY}
                x2={cropX + (displayCropW / 3) * n} y2={cropY + displayCropH}
                stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"
              />
              <line
                x1={cropX} y1={cropY + (displayCropH / 3) * n}
                x2={cropX + displayCropW} y2={cropY + (displayCropH / 3) * n}
                stroke="rgba(255,255,255,0.25)" strokeWidth="0.8"
              />
            </React.Fragment>
          ))}
        </svg>
      </div>

      <div style={{ textAlign: "center", fontSize: 12, color: "#888", margin: "6px 0 10px" }}>
        Output: <strong>{cropW} × {cropH} px</strong>
        {imgNatural.w ? ` | Original: ${imgNatural.w} × ${imgNatural.h} px` : ""}
        {" "}— <em>{hasTransparency ? "Transparent background preserved" : "White background applied"}</em>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>🔍 Zoom</span>
        <Slider
          min={minScale}
          max={maxScale}
          step={0.001}
          value={scale}
          onChange={handleZoomChange}
          style={{ flex: 1 }}
          tooltip={{ formatter: v => `${Math.round(v * 100)}%` }}
        />
        <span style={{ fontSize: 13, minWidth: 44, textAlign: "right" }}>
          {Math.round(scale * 100)}%
        </span>
      </div>
    </Modal>
  );
};

const UploadDragger = ({ onUpload, text, hint, disabled, accept = "image/*", style, maxWidth, maxHeight }) => (
  <Upload.Dragger accept={accept} showUploadList={false} disabled={disabled} style={style}
    beforeUpload={(file) => {
      const isValid = validateFileType(file, ["jpeg", "png", "jpg"], "Image");
      if (!isValid) return Upload.LIST_IGNORE;
      if (maxWidth && maxHeight) {
        return checkImageDimensions(file, maxWidth, maxHeight)
          .then(() => { onUpload(file); return false; })
          .catch(() => Upload.LIST_IGNORE);
      }
      onUpload(file);
      return false;
    }}>
    <p className="ant-upload-drag-icon"><InboxOutlined style={{ fontSize: 48 }} /></p>
    <p className="ant-upload-text">{text}</p>
    {hint && <p className="ant-upload-hint">{hint}</p>}
  </Upload.Dragger>
);

const ImageActions = ({ onReplace, onDelete, size = "small", style, maxWidth, maxHeight }) => (
  <div style={{ display: "flex", gap: 4, ...style }}>
    <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => {
      const isValid = validateFileType(file, ["jpeg", "png", "webp"], "Image");
      if (!isValid) return Upload.LIST_IGNORE;
      if (maxWidth && maxHeight) {
        return checkImageDimensions(file, maxWidth, maxHeight)
          .then(() => { onReplace(file); return false; })
          .catch(() => Upload.LIST_IGNORE);
      }
      onReplace(file);
      return false;
    }}>
      <Button size={size} icon={<EditOutlined />} />
    </Upload>
    <Button danger size={size} icon={<DeleteOutlined />} onClick={onDelete} />
  </div>
);

const NavButton = ({ icon, onClick, style, disabled }) => (
  <Button icon={icon} onClick={onClick} disabled={disabled} style={{ position: "absolute", ...style }} />
);

const CarouselNav = ({ onPrev, onNext, show, disabled, leftStyle, rightStyle }) => show && (
  <>
    <NavButton icon={<LeftOutlined />} onClick={onPrev} disabled={disabled} style={{ left: leftStyle?.left || -20, top: "45%", ...leftStyle }} />
    <NavButton icon={<RightOutlined />} onClick={onNext} disabled={disabled} style={{ right: rightStyle?.right || -20, top: "45%", ...rightStyle }} />
  </>
);


const LogoUpload = ({ logo, setLogo, title, isViewMode, directUpload, noImage, maxWidth, maxHeight }) => {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const openCropModal = (file) => {
    setPendingFile(file);
    setCropModalOpen(true);
  };

  const handleCropOk = (croppedFile) => {
    directUpload(croppedFile, setLogo);
    setCropModalOpen(false);
    setPendingFile(null);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setPendingFile(null);
  };

  return (
    <Col span={12}>
      {/* Crop modal */}
      <LogoCropModal
        open={cropModalOpen}
        file={pendingFile}
        onOk={handleCropOk}
        onCancel={handleCropCancel}
        defaultCropW={maxWidth}
        defaultCropH={maxHeight}
      />

      {!logo ? (
        <Upload.Dragger
          accept="image/*"
          showUploadList={false}
          disabled={isViewMode}
          beforeUpload={(file) => {
            const isValidType = validateFileType(file, ["jpeg", "png", "jpg"], "Image");
            if (!isValidType) return Upload.LIST_IGNORE;
            openCropModal(file);
            return false;
          }}
          style={{ padding: "20px 0" }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ fontSize: 48 }} />
          </p>
          <p className="ant-upload-text">
            Upload {title}
            <br />
            <span style={{ fontSize: 14 }}>
              Default crop: {maxWidth}px × {maxHeight}px (adjustable)
            </span>
          </p>
        </Upload.Dragger>
      ) : (
        <div style={{ textAlign: "center", padding: 20, border: "1px dashed #d9d9d9", borderRadius: 8 }}>
          <img
            src={buildImageUrl(logo)}
            alt={title}
            style={{ maxWidth: "100%", height: "auto" }}
            onError={(e) => (e.target.src = noImage)}
          />
          {!isViewMode && (
            <div style={{ marginTop: 10 }}>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  const isValidType = validateFileType(file, ["jpeg", "png", "jpg"], "Image");
                  if (!isValidType) return Upload.LIST_IGNORE;
                  openCropModal(file);
                  return false;
                }}
              >
                <Button icon={<EditOutlined />} style={{ marginRight: 8 }}>
                  Crop & Replace
                </Button>
              </Upload>
              <Button danger icon={<DeleteOutlined />} onClick={() => setLogo(null)}>
                Remove
              </Button>
            </div>
          )}
        </div>
      )}
    </Col>
  );
};


// ─── BannerCarousel ───────────────────────────────────────────────────────────
const BannerCarousel = ({ banners, setBanners, currentSlide, setCurrentSlide, title, isViewMode, directUpload, handleReplace, noImage, isMobile }) => {
  const maxBanners = 3;
  const containerStyle = isMobile ? { width: 300, margin: "0 auto", position: "relative" } : {};
  const canUpload = banners.length < maxBanners;
  const maxWidth = isMobile ? MOBILE_BANNER_WIDTH : WEB_BANNER_WIDTH;
  const maxHeight = isMobile ? MOBILE_BANNER_HEIGHT : WEB_BANNER_HEIGHT;

  return (
    <Card title={`${title} (Max ${maxBanners})`} style={{ marginTop: 40 }} headStyle={{ background: "#e6f7ff" }}>
      {!isViewMode && canUpload && (
        <UploadDragger
          onUpload={(f) => directUpload(f, setBanners)}
          text={<div>Upload {title.split(" ")[0]} Banner <br />
            <span style={{ fontSize: "14px" }}>Max size: {isMobile ? "390px" : "1920px"} × {isMobile ? "568px" : "780px"}</span>
          </div>}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
        />
      )}
      {!isViewMode && !canUpload && (
        <Alert
          message={`Maximum ${maxBanners} banners allowed. You can only replace or delete existing banners.`}
          type="warning" showIcon style={{ marginBottom: 16 }}
        />
      )}
      {banners.length > 0 && (
        <div style={{ marginTop: 20, textAlign: isMobile ? "center" : "left", position: "relative" }}>
          {banners.length > 1 && (
            <CarouselNav
              show
              onPrev={() => setCurrentSlide((p) => (p === 0 ? banners.length - 1 : p - 1))}
              onNext={() => setCurrentSlide((p) => (p + 1) % banners.length)}
              leftStyle={{ left: isMobile ? "25%" : 10, top: "45%" }}
              rightStyle={{ right: isMobile ? "25%" : 10, top: "45%" }}
            />
          )}
          <div style={containerStyle}>
            <img
              src={buildImageUrl(banners[currentSlide])}
              alt="banner"
              style={{ width: "100%", height: "auto", borderRadius: isMobile ? 8 : 0 }}
              onError={(e) => (e.target.src = noImage)}
            />
            {!isViewMode && (
              <div style={{ position: "absolute", top: 10, right: isMobile ? 10 : 60 }}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isValidType = validateFileType(file, ["jpeg", "png", "jpg"], "Image");
                    if (!isValidType) return Upload.LIST_IGNORE;
                    return checkImageDimensions(file, maxWidth, maxHeight)
                      .then(() => { handleReplace(file, currentSlide, isMobile ? "mobile-banner" : "web-banner"); return false; })
                      .catch(() => Upload.LIST_IGNORE);
                  }}
                >
                  <Button size={isMobile ? "small" : "default"} icon={<EditOutlined />} style={{ marginRight: isMobile ? 4 : 8 }}>
                    {!isMobile && "Replace"}
                  </Button>
                </Upload>
                <Button
                  size={isMobile ? "small" : "default"}
                  danger icon={<DeleteOutlined />}
                  onClick={() => {
                    setBanners((prev) => {
                      const updated = prev.filter((_, i) => i !== currentSlide);
                      setCurrentSlide(Math.max(0, Math.min(currentSlide, updated.length - 1)));
                      return updated;
                    });
                    message.success(`${isMobile ? "Mobile" : "Web"} banner deleted successfully!`);
                  }}
                >
                  {!isMobile && "Delete"}
                </Button>
              </div>
            )}
          </div>
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <Text type="secondary">Banner {currentSlide + 1} of {banners.length}</Text>
          </div>
        </div>
      )}
    </Card>
  );
};


const SectionCard = ({
  items, setItems, currentSlide, setCurrentSlide,
  title, maxImages, section, isViewMode,
  openModal, openEditModal, handleReplace, noImage, cssClass,
}) => {
  const canUpload = items.length < maxImages;
  const showNav = items.length > 3;
  const displayItems = showNav ? items.slice(currentSlide, currentSlide + 3) : items;
  const maxWidth = section === "culture" ? CULTURE_WIDTH : WHATWEDO_WIDTH;
  const maxHeight = section === "culture" ? CULTURE_HEIGHT : WHATWEDO_HEIGHT;

  const handleDeleteItem = (actualIndex) => {
    setItems((prev) => {
      const newItems = prev.filter((_, i) => i !== actualIndex);
      if (currentSlide >= newItems.length && newItems.length > 0)
        setCurrentSlide(Math.max(0, newItems.length - 1));
      return newItems;
    });
    message.success("Item deleted successfully!");
  };

  return (
    <Card title={`${title} - Max ${maxImages} Images`} style={{ marginTop: 40 }} headStyle={{ background: "#e6f7ff" }} className={cssClass}>
      {!isViewMode && canUpload && (
        <UploadDragger
          onUpload={(f) => openModal(section, f)}
          text={<div>Upload Image + Add Heading & Description <br />
            <span style={{ fontSize: "14px" }}>Max size: {section === "culture" ? "490px" : "736px"} × {section === "culture" ? "230px" : "250px"}</span>
          </div>}
          hint={`${items.length}/${maxImages} images uploaded`}
          maxWidth={maxWidth} maxHeight={maxHeight}
        />
      )}
      {!isViewMode && !canUpload && (
        <Alert message={`Maximum ${maxImages} images allowed. You can only replace or delete existing images.`} type="warning" showIcon style={{ marginBottom: 16 }} />
      )}
      {items.length > 0 && (
        <div style={{ marginTop: 30, position: "relative" }}>
          <CarouselNav
            show={showNav} disabled={isViewMode}
            onPrev={() => setCurrentSlide((p) => (p === 0 ? Math.max(items.length - 3, 0) : p - 1))}
            onNext={() => setCurrentSlide((p) => (p + 3 >= items.length ? 0 : p + 1))}
          />
          <Row gutter={24} justify="center">
            {displayItems.map((item, idx) => {
              const actualIndex = showNav ? currentSlide + idx : idx;
              return (
                <Col key={`${item.key}-${actualIndex}`} xs={24} sm={12} md={8}>
                  <div style={{ position: "relative", marginBottom: 20, borderRadius: 12, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", border: "1px solid #e8e8e8" }}>
                    <img src={buildImageUrl(item.key)} alt="" style={{ width: "100%", height: "auto" }} onError={(e) => (e.target.src = noImage)} />
                    {!isViewMode && (
                      <ImageActions
                        onReplace={(f) => handleReplace(f, actualIndex, section)}
                        onDelete={() => handleDeleteItem(actualIndex)}
                        style={{ position: "absolute", top: 8, right: 8 }}
                        maxWidth={maxWidth} maxHeight={maxHeight}
                      />
                    )}
                    <div style={{ padding: 16, background: "white" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <Title level={4} style={{ margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "150px" }} title={item.heading}>
                            {truncateText(item.heading, HEADING_MAX_LENGTH)}
                          </Title>
                          <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>{item.description}</Text>
                        </div>
                        {!isViewMode && (
                          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(item, actualIndex, section)} />
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
          {showNav && (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <Text type="secondary">Showing {currentSlide + 1}-{Math.min(currentSlide + 3, items.length)} of {items.length}</Text>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};


const HeadingDescModal = ({ open, onOk, onCancel, form, title, okText, loading, section }) => (
  <Modal title={title} open={open} onOk={onOk} onCancel={onCancel} okText={okText} width={520} confirmLoading={loading} okButtonProps={{ disabled: loading }} cancelButtonProps={{ disabled: loading }}>
    <Form form={form} layout="vertical">
      <Form.Item name="heading" label="Heading" rules={[{ required: true, message: "Heading is required" }, { max: HEADING_MAX_LENGTH, message: `Max ${HEADING_MAX_LENGTH} chars` }]}>
        <Input placeholder="e.g. Innovation First" />
      </Form.Item>
      <Form.Item name="description" label="Description" rules={[
        { required: true, message: "Description is required" },
        ...(section === "whatwedo" ? [{ type: "string", min: 100, max: 400, message: "Description must be between 100 and 400 characters" }] : []),
      ]}>
        <Input.TextArea rows={3} placeholder="We deliver care that respects diverse beliefs..." />
      </Form.Item>
    </Form>
  </Modal>
);

const RatingField = ({ name, label, disabled }) =>
  name === "reviews" ? (
    <Form.Item name="reviews" label="Reviews" rules={[{ type: "number", message: "Reviews must be a number" }, { type: "number", min: 0, message: "Reviews cannot be negative" }]}>
      <InputNumber min={0} disabled={disabled} style={{ width: "100%" }}
        parser={(v) => v.replace(/\D/g, "")}
        onKeyDown={(e) => !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(e.key) && e.preventDefault()}
      />
    </Form.Item>
  ) : (
    <Form.Item label={`${label} (out of 5)`}>
      <Form.Item name={name} noStyle rules={[{ type: "number", message: `${label} must be a number` }]}>
        <Rate allowHalf disabled={disabled} />
      </Form.Item>
      <Form.Item shouldUpdate noStyle>
        {({ getFieldValue }) => (
          <Text type="secondary" style={{ marginLeft: 8 }}>{Number(getFieldValue(name) || 0).toFixed(1)} / 5</Text>
        )}
      </Form.Item>
    </Form.Item>
  );

const CreatePtmClient = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { singleEmployer, signedUrlLoading, isLoading } = useSelector((state) => state.ptmAccounts);
  const isViewMode = mode === "view";
  const isEditMode = !!id && !isViewMode;
  const isCreateMode = !id;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState("");
  const [tempFile, setTempFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [webBanners, setWebBanners] = useState([]);
  const [mobileBanners, setMobileBanners] = useState([]);
  const [webLogo, setWebLogo] = useState(null);
  const [mobileLogo, setMobileLogo] = useState(null);
  const [cultureImages, setCultureImages] = useState([]);
  const [whatWeDoImages, setWhatWeDoImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [currentWebSlide, setCurrentWebSlide] = useState(0);
  const [currentMobileSlide, setCurrentMobileSlide] = useState(0);
  const [currentCultureSlide, setCurrentCultureSlide] = useState(0);
  const [currentWhatWeDoSlide, setCurrentWhatWeDoSlide] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingSection, setEditingSection] = useState("");
  const modalOkLock = useRef(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const getCurrentFolder = () => {
    if (!form.getFieldValue("subDomain")) {
      message.error("Please set the Subdomain before uploading files.");
      return;
    }
    return `content/${form.getFieldValue("subDomain").toLowerCase().trim()}`;
  };

  const sectionSetters = useMemo(() => ({
    culture: setCultureImages,
    whatwedo: setWhatWeDoImages,
    "web-banner": setWebBanners,
    "mobile-banner": setMobileBanners,
  }), []);

  const uploadToS3 = useCallback(async (file, isVideo = false) => {
    const folder = getCurrentFolder();
    const ext = file.name.split(".").pop();
    const fileName = `${isVideo ? "company_video_" : ""}${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${ext}`;
    const payload = { files: [{ fileName, fileType: file.type || (isVideo ? "video/mp4" : "application/octet-stream") }], folder };
    try {
      const result = await dispatch(generateSignedUrl(payload)).unwrap();
      await fetch(result.data[0].url, { method: "PUT", body: file, headers: { "Content-Type": file.type || (isVideo ? "video/mp4" : "application/octet-stream") } });
      const s3Key = result.data[0].key;
      message.success(`${isVideo ? "Video" : "Image"} uploaded successfully!`);
      return s3Key;
    } catch (err) {
      message.error(err?.message || `${isVideo ? "Video" : "Image"} upload failed. Please try again.`);
      return null;
    }
  }, [dispatch, form]);

  const directUpload = useCallback(async (file, setter) => {
    const key = await uploadToS3(file);
    if (key) setter((prev) => (Array.isArray(prev) ? [...prev, key] : key));
  }, [uploadToS3]);

  const uploadVideoToS3 = useCallback(async (file) => {
    try {
      setVideoUploading(true);
      const key = await uploadToS3(file, true);
      if (key) setVideoUrl(key);
    } finally {
      setVideoUploading(false);
    }
  }, [uploadToS3]);

  const openModal = useCallback((section, file) => {
    if (isViewMode) return;
    setCurrentSection(section);
    setTempFile(file);
    setIsModalOpen(true);
    modalForm.resetFields();
  }, [isViewMode, modalForm]);

  const handleModalOk = useCallback(async () => {
    if (modalOkLock.current) return;
    modalOkLock.current = true;
    setModalSubmitting(true);
    try {
      const { heading, description } = await modalForm.validateFields();
      const key = await uploadToS3(tempFile);
      if (key) {
        sectionSetters[currentSection]?.((p) => [...p, { key, heading, description }]);
        setIsModalOpen(false);
        setTempFile(null);
      }
    } catch (error) {
      console.error("Modal error:", error);
    } finally {
      setModalSubmitting(false);
      modalOkLock.current = false;
    }
  }, [modalForm, tempFile, currentSection, sectionSetters, uploadToS3]);

  const openEditModal = useCallback((item, index, section) => {
    if (isViewMode) return;
    setEditingItem(item);
    setEditingIndex(index);
    setEditingSection(section);
    editForm.setFieldsValue({ heading: item.heading, description: item.description });
    setIsEditModalOpen(true);
  }, [isViewMode, editForm]);

  const handleEditModalOk = useCallback(async () => {
    const { heading, description } = await editForm.validateFields();
    sectionSetters[editingSection]?.((p) => p.map((item, idx) => idx === editingIndex ? { ...editingItem, heading, description } : item));
    message.success("Updated successfully!");
    setIsEditModalOpen(false);
  }, [editForm, editingSection, editingIndex, editingItem, sectionSetters]);

  const handleReplaceImage = useCallback(async (file, index, section) => {
    const key = await uploadToS3(file);
    if (key) {
      sectionSetters[section]?.((p) => p.map((item, idx) => idx === index ? (typeof item === "string" ? key : { ...item, key }) : item));
    }
  }, [uploadToS3, sectionSetters]);

  const onGetDetailsSuccess = useCallback((response) => {
    if (!response?.data?.data || !id) return;
    const data = response.data.data;
    form.setFieldsValue({
      clientName: data.employerName || "",
      subDomain: data.subDomain || "",
      webUrl: data.webUrl || "",
      email: data.emailAddress || "",
      reviews: data.reviews ?? null,
      rating: data.rating ?? null,
      professionCode: data.professionCode || undefined,
      ...SOCIAL_CONFIG.reduce((acc, { key }) => ({ ...acc, [key]: data.socialMedia?.find((s) => s.type === key)?.link || "" }), {}),
    });
    if (data.logo?.desktop) setWebLogo(extractKeyFromUrl(data.logo.desktop));
    if (data.logo?.mobile) setMobileLogo(extractKeyFromUrl(data.logo.mobile));
    if (data.banner?.desktop) setWebBanners(data.banner.desktop.map(extractKeyFromUrl));
    if (data.banner?.mobile) setMobileBanners(data.banner.mobile.map(extractKeyFromUrl));
    if (data.video) setVideoUrl(extractKeyFromUrl(data.video));

    const mapItems = (items) => items?.map((i) => ({ key: extractKeyFromUrl(i.image), heading: i.text || "", description: i.desc || "" })) || [];
    const culture = data.sections?.find((s) => ["culture", "employersmission", "mission"].includes(s.type));
    const about = data.sections?.find((s) => ["about", "aboutus"].includes(s.type));
    if (culture?.items) setCultureImages(mapItems(culture.items));
    if (about?.items) setWhatWeDoImages(mapItems(about.items));
  }, [id, form]);

  useEffect(() => {
    if (id) dispatch(getSingleEmployerAccount({ id, callBack: onGetDetailsSuccess }));
  }, [id, dispatch, onGetDetailsSuccess]);

  const onSuccess = useCallback((respo) => {
    respo?.data?.backendPipline?.pipelineExecutionId
      ? navigate(`/provision/${respo.data.backendPipline.pipelineExecutionId}`)
      : message.error("Pipeline execution ID not found.");
  }, [navigate]);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const socialMedia = SOCIAL_CONFIG.filter(({ key }) => values[key]).map(({ key }) => ({ type: key, link: values[key] }));
      const sections = [];
      if (cultureImages.length > 0) {
        sections.push({
          type: "employersmission", title: "Our Mission",
          description: "We aim to innovate the future of talent marketplace.",
          items: cultureImages.map((i) => ({ image: i.key, text: i.heading, desc: i.description })),
        });
      }
      if (whatWeDoImages.length > 0) {
        sections.push({
          type: "aboutus", title: "About Us",
          description: "Know more about the company background.",
          items: whatWeDoImages.map((i) => ({ image: i.key, text: i.heading, desc: i.description })),
        });
      }
      const payload = {
        logo: { mobile: mobileLogo || "", desktop: webLogo || "" },
        banner: { mobile: mobileBanners || [], desktop: webBanners || [] },
        video: videoUrl || "",
        sections, socialMedia,
        subDomain: values.subDomain,
        webUrl: values.webUrl || "",
        clientname: values.clientName,
        email: values.email,
        professionCode: values.professionCode,
        reviews: values.reviews ?? null,
        rating: values.rating ?? null,
        ...(values.password && { password: values.password }),
      };
      if (isEditMode) {
        await dispatch(updateEmployerAccount({ id: singleEmployer?.data?._id, payload })).unwrap();
        message.success("Client Updated Successfully!");
        navigate("/manage-LandingPages");
      } else {
        await dispatch(createEmployerAccount({ ...payload, callBack: onSuccess })).unwrap();
        message.success("Deploying PTM Client...");
      }
    } catch (error) {
      message.error(error?.message || error?.error || "Error while saving PTM client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 30 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title level={3}>{isViewMode ? "View" : isEditMode ? "Edit" : "Create"} PTM Client</Title>
            <Text type="secondary">{isViewMode ? "All fields are read-only" : "Fill all required information"}</Text>
          </div>
          <Button style={{ position: "relative", bottom: "11px" }} type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate("/manage-LandingPages")} size="large">
            {isViewMode ? "Back to List" : "Cancel"}
          </Button>
        </div>
      </Card>

      <Spin spinning={signedUrlLoading || isLoading || loading} fullscreen />

      <Form layout="vertical" form={form} onFinish={onFinish} disabled={isViewMode}>
        {/* Basic Info */}
        <Row gutter={[20, 16]}>
          {[
            { name: "clientName", label: "Client Name", placeholder: "e.g. Engro", required: true },
            {
              name: "subDomain", label: "Sub Domain", placeholder: "engro", required: true, addonAfter: ".skillgigs.com",
              rules: [{ pattern: /^[a-z0-9]+$/, message: "Sub Domain can only contain lowercase letters and numbers (no dots or special characters)" }],
            },
            {
              name: "webUrl", label: "Company Website URL", placeholder: "https://www.companywebsite.com/...", required: true,
              rules: [{ validator: validateWebsiteUrl }],
            },
          ].map(({ name, label, placeholder, required, addonAfter, rules }) => (
            <Col xs={24} sm={24} md={12} lg={8} key={name}>
              <Form.Item
                name={name} label={label}
                rules={!isViewMode ? (required ? [{ required: true, message: `${label} is required` }, ...(rules || [])] : rules) : []}
              >
                <Input size="large" placeholder={placeholder} addonAfter={addonAfter}
                  disabled={isViewMode || (isEditMode && (name === "clientName" || name === "subDomain"))} />
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Row gutter={[20, 16]}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item name="email" label="Email" rules={[{ required: !isViewMode, message: "Email is required" }, { type: "email", message: "Enter valid email" }]}>
              <Input size="large" placeholder="dummy@gmail.com" disabled={isViewMode || isEditMode} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item name="password" label="Password" rules={isCreateMode ? [{ required: true, message: "Password is required on create" }] : []}>
              <Input.Password size="large" placeholder={isCreateMode ? "Enter password" : ""} disabled={isViewMode || isEditMode} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item name="professionCode" label="Profession Code" rules={[{ required: !isViewMode, message: "Select profession" }]}>
              <Select size="large" placeholder="Select profession" disabled={isViewMode || isEditMode}>
                <Option value="IT">IT</Option>
                <Option value="HC">HC</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Card title="Company Logo" style={{ marginTop: 40 }} headStyle={{ background: "#e6f7ff" }}>
          <Row gutter={40}>
            <LogoUpload
              logo={webLogo} setLogo={setWebLogo} title="Logo"
              isViewMode={isViewMode} directUpload={directUpload} noImage={NO_IMAGE}
              maxWidth={MAX_LOGO_WIDTH} maxHeight={MAX_LOGO_HEIGHT}
            />
          </Row>
        </Card>

        <BannerCarousel banners={webBanners} setBanners={setWebBanners} currentSlide={currentWebSlide} setCurrentSlide={setCurrentWebSlide}
          title="Web Banner Carousel" isViewMode={isViewMode} directUpload={directUpload} handleReplace={handleReplaceImage} noImage={NO_IMAGE} />
        <BannerCarousel banners={mobileBanners} setBanners={setMobileBanners} currentSlide={currentMobileSlide} setCurrentSlide={setCurrentMobileSlide}
          title="Mobile Banner Carousel" isViewMode={isViewMode} directUpload={directUpload} handleReplace={handleReplaceImage} noImage={NO_IMAGE} isMobile />

        <SectionCard
          items={cultureImages} setItems={setCultureImages}
          currentSlide={currentCultureSlide} setCurrentSlide={setCurrentCultureSlide}
          title="Our Culture Section" maxImages={CULTURE_MAX_IMAGES} section="culture"
          isViewMode={isViewMode} openModal={openModal} openEditModal={openEditModal}
          handleReplace={handleReplaceImage} noImage={NO_IMAGE} cssClass="culture-section-card"
        />

        <Form.Item
          name="whatWeDoImages"
          rules={[{
            validator: (_, value) => {
              if (!whatWeDoImages || whatWeDoImages.length === 0)
                return Promise.reject("Please add at least one item in Why Work Here section");
              return Promise.resolve();
            },
          }]}
        >
          <SectionCard
            items={whatWeDoImages} setItems={setWhatWeDoImages}
            currentSlide={currentWhatWeDoSlide} setCurrentSlide={setCurrentWhatWeDoSlide}
            title="What We Do / Why We Do" maxImages={WHATWEDO_MAX_IMAGES} section="whatwedo"
            isViewMode={isViewMode} openModal={openModal} openEditModal={openEditModal}
            handleReplace={handleReplaceImage} noImage={NO_IMAGE} cssClass="what-we-do-section-card"
          />
        </Form.Item>

        <Card title="Company Video Section" style={{ marginTop: 40 }} headStyle={{ background: "#e6f7ff" }}>
          <Text strong style={{ display: "block", marginBottom: 16 }}>Upload a company intro video</Text>
          <Spin spinning={videoUploading}>
            {!videoUrl ? (!isViewMode && (
              <Upload.Dragger accept="video/mp4,video/webm" showUploadList={false}
                beforeUpload={(file) => {
                  const isValid = validateFileType(file, ["mp4", "webm"], "Video");
                  if (!isValid) return Upload.LIST_IGNORE;
                  if (file.size / 1024 / 1024 >= 50) { message.error("Video must be smaller than 50MB!"); return Upload.LIST_IGNORE; }
                  uploadVideoToS3(file);
                  return false;
                }}>
                <p className="ant-upload-drag-icon"><InboxOutlined style={{ fontSize: 48 }} /></p>
                <p className="ant-upload-text">Click or drag video file to upload</p>
              </Upload.Dragger>
            )) : (
              <div style={{ textAlign: "center", padding: 20, background: "#fafafa", borderRadius: 8 }}>
                <video controls style={{ maxWidth: "100%", maxHeight: 500, borderRadius: 8, background: "#000" }} src={buildImageUrl(videoUrl)}>
                  Your browser does not support video.
                </video>
                {!isViewMode && (
                  <div style={{ marginTop: 16 }}>
                    <Upload accept="video/mp4,video/webm" showUploadList={false}
                      beforeUpload={(f) => { if (f.size / 1024 / 1024 >= 50) { message.error("Video must be smaller than 50MB!"); return Upload.LIST_IGNORE; } uploadVideoToS3(f); return false; }}>
                      <Button icon={<EditOutlined />} style={{ marginRight: 8 }}>Replace Video</Button>
                    </Upload>
                    <Button danger icon={<DeleteOutlined />} onClick={() => { setVideoUrl(null); message.success("Video deleted successfully!"); }}>
                      Remove Video
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Spin>
        </Card>

        <Card title="Review & Rating" style={{ marginTop: 40 }} headStyle={{ background: "#e6f7ff" }}>
          <Row gutter={24}>
            <Col xs={24} sm={12}><RatingField name="rating" label="Rating" disabled={isViewMode} /></Col>
            <Col xs={24} sm={12}><RatingField name="reviews" label="Review" disabled={isViewMode} /></Col>
          </Row>
        </Card>

        <Card title="Social Links (0 to 5)" style={{ marginTop: 40 }} headStyle={{ background: "#e6f7ff" }}>
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Leave all fields empty to hide this section, or provide up to 5 links.
          </Text>
          <Row gutter={16}>
            {SOCIAL_CONFIG.map(({ key, Icon, color, label }) => (
              <Col span={12} key={key}>
                <Form.Item
                  name={key}
                  label={<span style={{ marginTop: "10px" }}><Icon style={{ color, marginRight: 8 }} />{label}</span>}
                  rules={[{ validator: validateSocialUrl(key, label, `${key}.com`) }]}
                >
                  <Input placeholder={`https://www.${key}.com/...`} disabled={isViewMode} />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Card>

        {!isViewMode && (
          <div style={{ marginTop: 50, textAlign: "center" }}>
            <Button type="primary" htmlType="submit" size="large" style={{ width: 300, height: 50 }}>
              {isEditMode ? "Update PTM" : "Create PTM"}
            </Button>
          </div>
        )}
      </Form>

      <HeadingDescModal
        open={isModalOpen} onOk={handleModalOk}
        onCancel={() => { setIsModalOpen(false); setTempFile(null); }}
        form={modalForm} title="Add Heading & Description" okText="Upload & Add"
        loading={signedUrlLoading || modalSubmitting} section={currentSection}
      />
      <HeadingDescModal
        open={isEditModalOpen} onOk={handleEditModalOk} form={editForm}
        title="Edit Heading & Description" okText="Save Changes"
        onCancel={() => { setIsEditModalOpen(false); setEditingItem(null); setEditingIndex(null); setEditingSection(""); }}
        section={editingSection}
      />
    </>
  );
};

export default CreatePtmClient;